<template>
  <Card>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium text-gray-900 dark:text-white">Permissions & Scoping</h3>
      <Button variant="outline" size="sm" @click="refreshPermissions" :loading="isLoading">
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Refresh
      </Button>
    </div>

    <!-- Key type guard -->
    <div v-if="apiKey && apiKey.key_type === 'scope'" class="text-center py-8">
      <Alert variant="info">
        Scope-based keys use predefined permission sets and cannot be edited individually.
        To use custom permissions, create a fine-grained key instead.
      </Alert>
    </div>

    <div v-else-if="isLoading" class="flex items-center justify-center h-32">
      <LoadingSpinner />
    </div>

    <div v-else-if="error" class="text-center py-8">
      <Alert variant="error" :message="error" />
    </div>

    <div v-else>
      <!-- Permission Categories -->
      <div class="space-y-6">
        <div
          v-for="category in permissionCategories"
          :key="category.id"
          class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          <div class="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-white">{{ category.name }}</h4>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ category.description }}</p>
              </div>
              <div class="flex items-center space-x-2">
                <Button variant="outline" size="sm" @click="selectAllInCategory(category.id)">
                  Select All
                </Button>
                <Button variant="outline" size="sm" @click="clearAllInCategory(category.id)">
                  Clear All
                </Button>
              </div>
            </div>
          </div>

          <div class="p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="permission in category.permissions"
                :key="permission.id"
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer"
                :class="{
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/20': selectedPermissions.includes(permission.id),
                }"
                @click="togglePermission(permission.id)"
              >
                <div class="flex items-start">
                  <Checkbox
                    :model-value="selectedPermissions.includes(permission.id)"
                    class="mt-1 mr-3"
                    @click.stop
                    @update:model-value="togglePermission(permission.id)"
                  />
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <h5 class="text-sm font-medium text-gray-900 dark:text-white">{{ permission.name }}</h5>
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="getLevelBadgeColor(permission.level)"
                      >
                        {{ permission.level }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ permission.description }}</p>

                    <div v-if="permission.resources.length > 0" class="mt-2">
                      <div class="text-xs text-gray-400 mb-1">Resources:</div>
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="resource in permission.resources"
                          :key="resource"
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        >
                          {{ resource }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Permission Summary -->
      <div class="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Selected Permissions Summary</h4>

        <div v-if="selectedPermissions.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
          No permissions selected. This API key will have no access.
        </div>

        <div v-else class="space-y-2">
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-300">Total Permissions:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ selectedPermissions.length }}</span>
          </div>

          <div class="grid grid-cols-5 gap-4 text-center">
            <div class="bg-white dark:bg-gray-800 rounded p-2">
              <div class="text-lg font-bold text-green-600">
                {{ getPermissionCountByLevel('read') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Read</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded p-2">
              <div class="text-lg font-bold text-blue-600">
                {{ getPermissionCountByLevel('write') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Write</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded p-2">
              <div class="text-lg font-bold text-purple-600">
                {{ getPermissionCountByLevel('execute') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Execute</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded p-2">
              <div class="text-lg font-bold text-orange-600">
                {{ getPermissionCountByLevel('manage') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Manage</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded p-2">
              <div class="text-lg font-bold text-red-600">
                {{ getPermissionCountByLevel('admin') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Admin</div>
            </div>
          </div>

          <!-- Selected permissions list -->
          <div class="mt-3">
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">Selected:</div>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="permissionId in selectedPermissions"
                :key="permissionId"
                class="inline-flex items-center px-2 py-1 rounded text-xs font-medium font-mono bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
              >
                {{ permissionId }}
                <button
                  @click="removePermission(permissionId)"
                  class="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  &times;
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Templates -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Templates</h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="outline"
            size="sm"
            @click="applyTemplate('readonly')"
            class="justify-start"
          >
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Read Only
          </Button>

          <Button
            variant="outline"
            size="sm"
            @click="applyTemplate('member')"
            class="justify-start"
          >
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Member
          </Button>

          <Button
            variant="outline"
            size="sm"
            @click="applyTemplate('admin')"
            class="justify-start"
          >
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Admin
          </Button>

          <Button
            variant="outline"
            size="sm"
            @click="clearAllPermissions"
            class="justify-start text-red-600 hover:text-red-700"
          >
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Clear All
          </Button>
        </div>
      </div>

      <!-- Save Actions -->
      <div class="mt-6 flex items-center justify-between">
        <div v-if="hasChanges" class="text-sm text-yellow-600 dark:text-yellow-400">
          Unsaved changes
        </div>
        <div v-else class="text-sm text-gray-400"></div>
        <div class="flex items-center space-x-3">
          <Button variant="outline" @click="resetPermissions" :disabled="!hasChanges"> Reset </Button>
          <Button @click="savePermissions" :loading="saving" :disabled="!hasChanges"> Save Permissions </Button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { Button, LoadingSpinner, Alert, Card, Checkbox, useNotification } from '@asabdev/asab-ui';
  import { apiKeysApi } from '@/lib/api/apiKeys';
  import { useOrganizationsStore } from '@/stores/organizations';
  import type { ApiKey, PermissionCategory } from '../types';

  interface Props {
    apiKey?: ApiKey | null;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    'permissions-updated': [permissions: string[]];
  }>();

  const { showSuccess, showError } = useNotification();
  const orgStore = useOrganizationsStore();

  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const saving = ref(false);
  const selectedPermissions = ref<string[]>([]);
  const initialPermissions = ref<string[]>([]);
  const permissionCategories = ref<PermissionCategory[]>([]);
  const rolePermissions = ref<Record<string, string[]>>({});

  const hasChanges = computed(() => {
    if (selectedPermissions.value.length !== initialPermissions.value.length) return true;
    const sorted1 = [...selectedPermissions.value].sort();
    const sorted2 = [...initialPermissions.value].sort();
    return sorted1.some((v, i) => v !== sorted2[i]);
  });

  // Build a flat lookup: permission ID → level
  const permissionLevelMap = computed(() => {
    const map: Record<string, string> = {};
    for (const cat of permissionCategories.value) {
      for (const p of cat.permissions) {
        map[p.id] = p.level;
      }
    }
    return map;
  });

  const refreshPermissions = async () => {
    const orgId = orgStore.currentOrganization?.id;
    if (!orgId) {
      error.value = 'No organization selected';
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;

      const data = await apiKeysApi.getPermissionCategories(orgId);
      permissionCategories.value = data.categories;
      rolePermissions.value = data.role_permissions;

      // Set initial permissions from the key
      if (props.apiKey?.permissions) {
        selectedPermissions.value = [...props.apiKey.permissions];
        initialPermissions.value = [...props.apiKey.permissions];
      } else {
        selectedPermissions.value = [];
        initialPermissions.value = [];
      }
    } catch (err) {
      error.value = (err as Error).message || 'Failed to fetch permissions';
    } finally {
      isLoading.value = false;
    }
  };

  const getLevelBadgeColor = (level: string): string => {
    switch (level) {
      case 'admin':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
      case 'manage':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200';
      case 'write':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
      case 'execute':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200';
      default:
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
    }
  };

  const getPermissionCountByLevel = (level: string): number => {
    return selectedPermissions.value.filter(id => permissionLevelMap.value[id] === level).length;
  };

  const togglePermission = (permissionId: string) => {
    const idx = selectedPermissions.value.indexOf(permissionId);
    if (idx >= 0) {
      selectedPermissions.value.splice(idx, 1);
    } else {
      selectedPermissions.value.push(permissionId);
    }
  };

  const selectAllInCategory = (categoryId: string) => {
    const category = permissionCategories.value.find(c => c.id === categoryId);
    if (category) {
      const catPerms = category.permissions.map(p => p.id);
      const existing = new Set(selectedPermissions.value);
      for (const p of catPerms) existing.add(p);
      selectedPermissions.value = [...existing];
    }
  };

  const clearAllInCategory = (categoryId: string) => {
    const category = permissionCategories.value.find(c => c.id === categoryId);
    if (category) {
      const catPerms = new Set<string>(category.permissions.map(p => p.id));
      selectedPermissions.value = selectedPermissions.value.filter(id => !catPerms.has(id));
    }
  };

  const removePermission = (permissionId: string) => {
    selectedPermissions.value = selectedPermissions.value.filter(id => id !== permissionId);
  };

  const applyTemplate = (template: string) => {
    const perms = rolePermissions.value[template];
    if (perms) {
      // Role template (member, admin, etc.)
      selectedPermissions.value = [...perms];
    } else if (template === 'readonly') {
      // Read-only: all *:read permissions
      const readPerms: string[] = [];
      for (const cat of permissionCategories.value) {
        for (const p of cat.permissions) {
          if (p.level === 'read') readPerms.push(p.id);
        }
      }
      selectedPermissions.value = readPerms;
    }
  };

  const clearAllPermissions = () => {
    selectedPermissions.value = [];
  };

  const resetPermissions = () => {
    selectedPermissions.value = [...initialPermissions.value];
  };

  const savePermissions = async () => {
    const orgId = orgStore.currentOrganization?.id;
    const keyId = props.apiKey?.id;
    if (!orgId || !keyId) return;

    try {
      saving.value = true;
      await apiKeysApi.updatePermissions(orgId, keyId, selectedPermissions.value);
      initialPermissions.value = [...selectedPermissions.value];
      emit('permissions-updated', selectedPermissions.value);
      showSuccess('Permissions updated successfully');
    } catch {
      showError('Failed to save permissions');
    } finally {
      saving.value = false;
    }
  };

  // Reload when apiKey prop changes
  watch(() => props.apiKey?.id, () => {
    if (props.apiKey) refreshPermissions();
  });

  onMounted(() => {
    if (props.apiKey) refreshPermissions();
  });
</script>
